/**
 * Star-trail animation for .call-to-action sections.
 *
 * WebGL point-sprite starfield: every star is a gl.POINT whose position is
 * computed in the vertex shader from static per-star attributes plus a time
 * uniform, so the CPU does no per-frame particle work. Long-exposure trails
 * come from ping-pong framebuffer feedback with continuous exponential decay,
 * which reaches a visual steady state — the animation loops forever and never
 * needs a reset.
 *
 * initStars() is idempotent: it tears down every prior instance before
 * binding the canvases on the current page, which is what makes it safe to
 * call from init_on_load on every Barba transition.
 */
import { createMotionToggle, setTogglePressed } from "./motion-toggle.js";
import { debounce, watchMediaQuery, prefersReducedMotion, REDUCED_MOTION_QUERY } from "./util.js";

const CONFIG = {
    dprCap: 2,
    areaPerStar: 900,     // px² of section per star
    countMin: 300,
    countMax: 1200,
    heroFraction: 0.015,  // share of stars rendered large
    tau: 6.0,             // trail decay time constant (s)
    rimSpeedPx: 22,       // linear speed at the outermost radius (px/s)
    stampGain: 14.0,      // light energy deposited per second
    innerGainFloor: 0.3,  // gain scale for the slowest (innermost) stars
    exposure: 1.1,        // tonemap knee
    twinkleAmp: 0.22,
    vignette: 0.35,
    resizeDebounceMs: 200,
    maxDt: 0.1,           // clamp for resume/hitch frames (s)
    prewarm: [{ steps: 90, dt: 1 / 30 }, { steps: 60, dt: 0.15 }],
    gold: [0xcf / 255, 0xab / 255, 0x57 / 255],
    silver: [0xac / 255, 0xad / 255, 0xb1 / 255],
};

const SEED = 0x5eed;

const VS_STARS = `
attribute vec2 a_polar;   // radius (css px), base angle (rad)
attribute vec2 a_kin;     // angular speed (rad/s), point size (css px)
attribute vec3 a_color;
attribute vec2 a_tw;      // twinkle speed (rad/s), twinkle phase
uniform vec2 u_sizeCss;
uniform float u_dpr;
uniform float u_maxPoint;
uniform float u_twinkleAmp;
uniform float u_time;
uniform float u_gain;
varying vec3 v_color;
void main() {
    float ang = a_polar.y + a_kin.x * u_time;
    vec2 pos = u_sizeCss * 0.5 + vec2(cos(ang), sin(ang)) * a_polar.x;
    vec2 ndc = pos / u_sizeCss * 2.0 - 1.0;
    gl_Position = vec4(ndc.x, -ndc.y, 0.0, 1.0);
    gl_PointSize = min(a_kin.y * u_dpr, u_maxPoint);
    float tw = 1.0 + u_twinkleAmp * sin(u_time * a_tw.x + a_tw.y);
    v_color = a_color * tw * u_gain;
}
`;

const FS_STARS = `
precision mediump float;
varying vec3 v_color;
void main() {
    vec2 p = gl_PointCoord - 0.5;
    float d2 = dot(p, p) * 4.0;
    float i = exp(-d2 * 18.0) + 0.28 * exp(-d2 * 3.5);
    gl_FragColor = vec4(v_color * i, 1.0);
}
`;

const VS_QUAD = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
    v_uv = a_pos * 0.5 + 0.5;
    gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

// The dither terms only apply on the 8-bit fallback (u_ditherAmp = 0 on the
// half-float path): ±0.5 LSB temporal noise keeps quantized decay unbiased so
// faint texels don't freeze into permanent haze, and the small subtract makes
// zero an absorbing state so the buffer settles at true black.
const FS_DECAY = `
precision mediump float;
uniform sampler2D u_prev;
uniform float u_decay;
uniform float u_ditherAmp;
uniform float u_seed;
varying vec2 v_uv;
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(12.9898, 78.233)) + u_seed) * 43758.5453);
}
void main() {
    vec3 v = texture2D(u_prev, v_uv).rgb * u_decay;
    v += vec3(hash(gl_FragCoord.xy) - 0.5) * u_ditherAmp;
    v = max(v - vec3(u_ditherAmp * 0.15), vec3(0.0));
    gl_FragColor = vec4(v, 1.0);
}
`;

// The vignette scales the trail light only — the background stays flat
// edge-to-edge so the canvas can never show a seam against the section.
const FS_PRESENT = `
precision mediump float;
uniform sampler2D u_trail;
uniform vec3 u_bg;
uniform float u_exposure;
uniform float u_vignette;
varying vec2 v_uv;
void main() {
    vec3 t = texture2D(u_trail, v_uv).rgb;
    t = 1.0 - exp(-t * u_exposure);
    float d = length(v_uv - 0.5) * 2.0;
    t *= 1.0 - u_vignette * smoothstep(0.55, 1.15, d);
    gl_FragColor = vec4(u_bg + t, 1.0);
}
`;

function mulberry32(seed) {
    var a = seed >>> 0;
    return function () {
        a = (a + 0x6d2b79f5) | 0;
        var t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function parseBgColor(el) {
    var m = (getComputedStyle(el).backgroundColor || "").match(/[\d.]+/g);
    if (!m || m.length < 3 || (m.length > 3 && parseFloat(m[3]) === 0)) {
        return [30 / 255, 30 / 255, 30 / 255];
    }
    return [m[0] / 255, m[1] / 255, m[2] / 255];
}

function compileShader(gl, type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        var info = gl.getShaderInfoLog(sh);
        gl.deleteShader(sh);
        throw new Error("stars.js shader compile failed: " + info);
    }
    return sh;
}

function linkProgram(gl, vsSrc, fsSrc, uniforms, attribs) {
    var vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
    var fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
        var info = gl.getProgramInfoLog(prog);
        gl.deleteProgram(prog);
        throw new Error("stars.js program link failed: " + info);
    }
    var u = {};
    var a = {};
    uniforms.forEach(function (n) { u[n] = gl.getUniformLocation(prog, n); });
    attribs.forEach(function (n) { a[n] = gl.getAttribLocation(prog, n); });
    return { prog: prog, u: u, a: a };
}

/**
 * Deterministic star field. Interleaved layout, 9 floats per star:
 * [r, theta0, omega, size, colR, colG, colB, twSpeed, twPhase]
 * Per-star gain equalization (gainScale) is baked into the color so slow
 * inner stars don't integrate to blown-out trails while rim stars stay dim.
 */
function buildStars(cssW, cssH, rand) {
    var count = Math.min(
        CONFIG.countMax,
        Math.max(CONFIG.countMin, Math.round((cssW * cssH) / CONFIG.areaPerStar))
    );
    var rMax = 0.5 * Math.hypot(cssW, cssH);
    var rMin = 0.06 * rMax;
    var omegaBase = CONFIG.rimSpeedPx / rMax;
    var heroCount = Math.ceil(count * CONFIG.heroFraction);
    var data = new Float32Array(count * 9);
    var o = 0;
    for (var i = 0; i < count; i++) {
        var r = rMin + (rMax - rMin) * Math.sqrt(rand());
        var theta0 = rand() * Math.PI * 2;
        var omega = omegaBase * (0.9 + 0.2 * rand());
        var speedNorm = Math.min(1, (omega * r) / CONFIG.rimSpeedPx);
        var gainScale =
            CONFIG.innerGainFloor + (1 - CONFIG.innerGainFloor) * Math.pow(speedNorm, 0.7);
        var mixRaw = rand();
        var mixT = mixRaw < 0.5 ? mixRaw * 0.4 : 1 - (1 - mixRaw) * 0.4;
        var b = rand();
        var brightness = 0.25 + 0.75 * b * b;
        var isHero = i >= count - heroCount;
        var size = isHero ? 5 + rand() * 4 : 1.4 + brightness * 0.9;
        var lum = brightness * (isHero ? 1.3 : 1) * gainScale;
        data[o++] = r;
        data[o++] = theta0;
        data[o++] = omega;
        data[o++] = size;
        for (var c = 0; c < 3; c++) {
            var base = CONFIG.silver[c] + (CONFIG.gold[c] - CONFIG.silver[c]) * mixT;
            data[o++] = base * (0.95 + 0.1 * rand()) * lum;
        }
        data[o++] = 0.5 + rand() * 2.0;
        data[o++] = rand() * Math.PI * 2;
    }
    return { count: count, data: data };
}

function createStarfield(canvas) {
    var section = canvas.closest(".call-to-action");
    if (!section) return null;

    var glOpts = {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        preserveDrawingBuffer: false,
        powerPreference: "low-power",
    };
    var gl = null;
    try {
        gl = canvas.getContext("webgl", glOpts) || canvas.getContext("experimental-webgl", glOpts);
    } catch (err) {
        gl = null;
    }
    if (!gl) return createStaticFallback(canvas, section);

    // ---- state ----
    var destroyed = false;
    var contextLost = false;
    var running = false;
    var userPaused = false;
    var userExplicitlyToggled = false;
    var intersecting = false;
    var rafId = null;
    var lastNow = null;
    var virtualTime = 0;
    var cssW = 0;
    var cssH = 0;
    var dprUsed = 1;
    var rawW = -1;
    var rawH = -1;
    var rawDpr = -1;
    var bufW = 0;
    var bufH = 0;
    var starCount = 0;

    // ---- GL objects ----
    var progStars = null;
    var progDecay = null;
    var progPresent = null;
    var quadBuf = null;
    var starBuf = null;
    var targets = null;
    var halfFloatType = 0;

    function releaseTarget(t) {
        if (!t) return;
        gl.deleteFramebuffer(t.fbo);
        gl.deleteTexture(t.tex);
    }

    function releaseTargets() {
        if (!targets) return;
        releaseTarget(targets.src);
        releaseTarget(targets.dst);
        targets = null;
    }

    function releaseGL() {
        releaseTargets();
        [progStars, progDecay, progPresent].forEach(function (p) {
            if (p) gl.deleteProgram(p.prog);
        });
        progStars = progDecay = progPresent = null;
        if (quadBuf) gl.deleteBuffer(quadBuf);
        if (starBuf) gl.deleteBuffer(starBuf);
        quadBuf = starBuf = null;
    }

    function makeTarget(w, h, type) {
        var tex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, type, null);
        var fbo = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
        var ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
        return { tex: tex, fbo: fbo, ok: ok };
    }

    // Render-to-half-float support varies by device and is only provable by
    // attaching one and checking completeness, so probe with a tiny target.
    function detectTrailFormat() {
        halfFloatType = 0;
        var ext = gl.getExtension("OES_texture_half_float");
        if (!ext) return;
        gl.getExtension("EXT_color_buffer_half_float");
        var probe = makeTarget(4, 4, ext.HALF_FLOAT_OES);
        if (probe.ok) halfFloatType = ext.HALF_FLOAT_OES;
        releaseTarget(probe);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    function sizeCanvas() {
        cssW = Math.max(1, section.clientWidth);
        cssH = Math.max(1, section.clientHeight);
        rawW = section.clientWidth;
        rawH = section.clientHeight;
        rawDpr = window.devicePixelRatio || 1;
        var maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE);
        dprUsed = Math.min(CONFIG.dprCap, rawDpr, maxTex / cssW, maxTex / cssH);
        canvas.width = Math.max(1, Math.round(cssW * dprUsed));
        canvas.height = Math.max(1, Math.round(cssH * dprUsed));
        bufW = gl.drawingBufferWidth;
        bufH = gl.drawingBufferHeight;
    }

    function syncStaticUniforms() {
        var range = gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE);
        var bg = parseBgColor(section);
        gl.useProgram(progStars.prog);
        gl.uniform2f(progStars.u.u_sizeCss, cssW, cssH);
        gl.uniform1f(progStars.u.u_dpr, dprUsed);
        gl.uniform1f(progStars.u.u_maxPoint, range ? range[1] : 64);
        gl.uniform1f(progStars.u.u_twinkleAmp, CONFIG.twinkleAmp);
        gl.useProgram(progPresent.prog);
        gl.uniform3f(progPresent.u.u_bg, bg[0], bg[1], bg[2]);
        gl.uniform1f(progPresent.u.u_exposure, CONFIG.exposure);
        gl.uniform1f(progPresent.u.u_vignette, CONFIG.vignette);
    }

    function seedStars() {
        var built = buildStars(cssW, cssH, mulberry32(SEED));
        starCount = built.count;
        gl.bindBuffer(gl.ARRAY_BUFFER, starBuf);
        gl.bufferData(gl.ARRAY_BUFFER, built.data, gl.STATIC_DRAW);
    }

    function allocTargets() {
        releaseTargets();
        var type = halfFloatType || gl.UNSIGNED_BYTE;
        var a = makeTarget(bufW, bufH, type);
        var b = makeTarget(bufW, bufH, type);
        if (halfFloatType && (!a.ok || !b.ok)) {
            releaseTarget(a);
            releaseTarget(b);
            halfFloatType = 0;
            a = makeTarget(bufW, bufH, gl.UNSIGNED_BYTE);
            b = makeTarget(bufW, bufH, gl.UNSIGNED_BYTE);
        }
        targets = { src: a, dst: b };
        gl.clearColor(0, 0, 0, 1);
        gl.bindFramebuffer(gl.FRAMEBUFFER, a.fbo);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.bindFramebuffer(gl.FRAMEBUFFER, b.fbo);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.useProgram(progDecay.prog);
        gl.uniform1f(progDecay.u.u_ditherAmp, halfFloatType ? 0 : 1.2 / 255);
    }

    function drawStars() {
        var a = progStars.a;
        gl.bindBuffer(gl.ARRAY_BUFFER, starBuf);
        gl.enableVertexAttribArray(a.a_polar);
        gl.vertexAttribPointer(a.a_polar, 2, gl.FLOAT, false, 36, 0);
        gl.enableVertexAttribArray(a.a_kin);
        gl.vertexAttribPointer(a.a_kin, 2, gl.FLOAT, false, 36, 8);
        gl.enableVertexAttribArray(a.a_color);
        gl.vertexAttribPointer(a.a_color, 3, gl.FLOAT, false, 36, 16);
        gl.enableVertexAttribArray(a.a_tw);
        gl.vertexAttribPointer(a.a_tw, 2, gl.FLOAT, false, 36, 28);
        gl.drawArrays(gl.POINTS, 0, starCount);
        gl.disableVertexAttribArray(a.a_polar);
        gl.disableVertexAttribArray(a.a_kin);
        gl.disableVertexAttribArray(a.a_color);
        gl.disableVertexAttribArray(a.a_tw);
    }

    function drawQuad(p) {
        gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
        gl.enableVertexAttribArray(p.a.a_pos);
        gl.vertexAttribPointer(p.a.a_pos, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.disableVertexAttribArray(p.a.a_pos);
    }

    // One simulation step: decay the previous trail into the other target,
    // stamp the stars additively on top, then swap so src holds the latest.
    function step(dt) {
        var t = targets;
        gl.bindFramebuffer(gl.FRAMEBUFFER, t.dst.fbo);
        gl.viewport(0, 0, bufW, bufH);
        gl.disable(gl.BLEND);
        gl.useProgram(progDecay.prog);
        gl.uniform1f(progDecay.u.u_decay, Math.exp(-dt / CONFIG.tau));
        gl.uniform1f(progDecay.u.u_seed, (virtualTime % 61) + 1);
        gl.bindTexture(gl.TEXTURE_2D, t.src.tex);
        drawQuad(progDecay);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        gl.useProgram(progStars.prog);
        gl.uniform1f(progStars.u.u_time, virtualTime);
        gl.uniform1f(progStars.u.u_gain, CONFIG.stampGain * dt);
        drawStars();
        gl.disable(gl.BLEND);
        targets = { src: t.dst, dst: t.src };
    }

    function present() {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, bufW, bufH);
        gl.disable(gl.BLEND);
        gl.useProgram(progPresent.prog);
        gl.bindTexture(gl.TEXTURE_2D, targets.src.tex);
        drawQuad(progPresent);
    }

    // Star positions are closed-form in time, so trail history can be built
    // analytically: re-stamp the field at past instants with the decay each
    // stamp would have undergone since. Two tiers keep it cheap — dense for
    // the bright recent trail, sparse (glow-bridged) for the faint old tail.
    function prewarm() {
        gl.bindFramebuffer(gl.FRAMEBUFFER, targets.src.fbo);
        gl.viewport(0, 0, bufW, bufH);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        gl.useProgram(progStars.prog);
        var elapsed = 0;
        for (var i = 0; i < CONFIG.prewarm.length; i++) {
            var tier = CONFIG.prewarm[i];
            for (var s = 0; s < tier.steps; s++) {
                elapsed += tier.dt;
                gl.uniform1f(progStars.u.u_time, -elapsed);
                gl.uniform1f(
                    progStars.u.u_gain,
                    CONFIG.stampGain * tier.dt * Math.exp(-elapsed / CONFIG.tau)
                );
                drawStars();
            }
        }
        gl.disable(gl.BLEND);
        virtualTime = 0;
        lastNow = null;
    }

    function rebuild() {
        sizeCanvas();
        syncStaticUniforms();
        seedStars();
        allocTargets();
        prewarm();
        present();
    }

    function setupGL() {
        releaseGL();
        progStars = linkProgram(
            gl, VS_STARS, FS_STARS,
            ["u_sizeCss", "u_dpr", "u_maxPoint", "u_twinkleAmp", "u_time", "u_gain"],
            ["a_polar", "a_kin", "a_color", "a_tw"]
        );
        progDecay = linkProgram(
            gl, VS_QUAD, FS_DECAY,
            ["u_prev", "u_decay", "u_ditherAmp", "u_seed"],
            ["a_pos"]
        );
        progPresent = linkProgram(
            gl, VS_QUAD, FS_PRESENT,
            ["u_trail", "u_bg", "u_exposure", "u_vignette"],
            ["a_pos"]
        );
        quadBuf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        starBuf = gl.createBuffer();
        gl.useProgram(progDecay.prog);
        gl.uniform1i(progDecay.u.u_prev, 0);
        gl.useProgram(progPresent.prog);
        gl.uniform1i(progPresent.u.u_trail, 0);
        gl.activeTexture(gl.TEXTURE0);
        detectTrailFormat();
        rebuild();
    }

    function frame(now) {
        rafId = requestAnimationFrame(frame);
        if (lastNow === null) {
            lastNow = now;
            return;
        }
        var dt = (now - lastNow) / 1000;
        lastNow = now;
        if (dt <= 0) return;
        if (dt > CONFIG.maxDt) dt = CONFIG.maxDt;
        virtualTime += dt;
        step(dt);
        present();
    }

    function updateRunning() {
        var should = !destroyed && !contextLost && !userPaused && intersecting && !document.hidden;
        if (should === running) return;
        running = should;
        if (running) {
            lastNow = null;
            rafId = requestAnimationFrame(frame);
        } else if (rafId !== null) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    }

    function checkSize() {
        if (destroyed || contextLost) return;
        var w = section.clientWidth;
        var h = section.clientHeight;
        var d = window.devicePixelRatio || 1;
        if (w === rawW && h === rawH && d === rawDpr) return;
        rebuild();
    }

    // ---- GL bring-up (before any DOM/listener side effects) ----
    try {
        setupGL();
    } catch (err) {
        // Rare driver/shader failure: release the context and fall back to a
        // fresh canvas node — this one can no longer yield a 2D context.
        try {
            var lose = gl.getExtension("WEBGL_lose_context");
            if (lose) lose.loseContext();
        } catch (err2) { /* ignore */ }
        var fresh = canvas.cloneNode(false);
        canvas.parentNode.replaceChild(fresh, canvas);
        return createStaticFallback(fresh, section);
    }

    // ---- pause/play button ----
    function setUserPaused(v) {
        userPaused = v;
        setTogglePressed(button, v);
        updateRunning();
    }

    function onToggle() {
        userExplicitlyToggled = true;
        setUserPaused(!userPaused);
    }

    var button = createMotionToggle("Pause background animation", onToggle);
    section.appendChild(button);

    // ---- reduced motion ----
    function onRmChange(e) {
        if (e.matches) setUserPaused(true);
        else if (!userExplicitlyToggled) setUserPaused(false);
    }

    var unwatchRm = watchMediaQuery(REDUCED_MOTION_QUERY, onRmChange);
    setUserPaused(prefersReducedMotion());

    // ---- resize / DPR ----
    var debouncedCheck = debounce(checkSize, CONFIG.resizeDebounceMs);
    var ro = null;
    if (typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(debouncedCheck);
        ro.observe(section);
    } else {
        window.addEventListener("resize", debouncedCheck);
    }

    // A ResizeObserver can't see the window moving to a display with a
    // different devicePixelRatio, so watch a resolution media query that
    // re-registers itself against the new ratio every time it flips.
    var unwatchDpr = null;

    function onDprChange() {
        checkSize();
        watchDpr();
    }

    function watchDpr() {
        if (unwatchDpr) unwatchDpr();
        unwatchDpr = watchMediaQuery(
            "(resolution: " + (window.devicePixelRatio || 1) + "dppx)",
            onDprChange
        );
    }

    watchDpr();

    // ---- visibility gates ----
    var io = null;
    if (typeof IntersectionObserver !== "undefined") {
        io = new IntersectionObserver(
            function (entries) {
                intersecting = entries[entries.length - 1].isIntersecting;
                updateRunning();
            },
            { rootMargin: "100px", threshold: 0 }
        );
        io.observe(section);
    } else {
        intersecting = true;
        updateRunning();
    }

    function onVisChange() {
        updateRunning();
    }

    document.addEventListener("visibilitychange", onVisChange);

    // ---- context loss ----
    function onContextLost(e) {
        e.preventDefault();
        contextLost = true;
        updateRunning();
    }

    function onContextRestored() {
        contextLost = false;
        try {
            setupGL();
        } catch (err) {
            contextLost = true;
            return;
        }
        updateRunning();
    }

    canvas.addEventListener("webglcontextlost", onContextLost, false);
    canvas.addEventListener("webglcontextrestored", onContextRestored, false);

    function destroy() {
        if (destroyed) return;
        destroyed = true;
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = null;
        if (ro) ro.disconnect();
        else window.removeEventListener("resize", debouncedCheck);
        if (io) io.disconnect();
        unwatchRm();
        if (unwatchDpr) unwatchDpr();
        document.removeEventListener("visibilitychange", onVisChange);
        canvas.removeEventListener("webglcontextlost", onContextLost, false);
        canvas.removeEventListener("webglcontextrestored", onContextRestored, false);
        button.removeEventListener("click", onToggle);
        button.remove();
        releaseGL();
        // Browsers cap live WebGL contexts; Barba replaces the canvas on every
        // navigation, so orphaned contexts must be released explicitly or the
        // newest canvas starts failing after a handful of page transitions.
        try {
            var loseExt = gl.getExtension("WEBGL_lose_context");
            if (loseExt && !gl.isContextLost()) loseExt.loseContext();
        } catch (err) { /* ignore */ }
    }

    return { destroy: destroy };
}

// Static long-exposure rendering for browsers without WebGL: the same star
// field drawn once with Canvas2D as faded arcs + head dots. Nothing moves, so
// no pause button is injected.
function createStaticFallback(canvas, section) {
    var ctx = null;
    try {
        ctx = canvas.getContext("2d");
    } catch (err) {
        ctx = null;
    }
    if (!ctx) return null;

    var destroyed = false;

    function chan(v) {
        return Math.round(255 * Math.min(1, v * 2.5));
    }

    function render() {
        if (destroyed) return;
        var cssW = Math.max(1, section.clientWidth);
        var cssH = Math.max(1, section.clientHeight);
        var dpr = Math.min(CONFIG.dprCap, window.devicePixelRatio || 1);
        canvas.width = Math.round(cssW * dpr);
        canvas.height = Math.round(cssH * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cssW, cssH);
        var built = buildStars(cssW, cssH, mulberry32(SEED));
        var cx = cssW / 2;
        var cy = cssH / 2;
        for (var i = 0; i < built.count; i++) {
            var o = i * 9;
            var r = built.data[o];
            var theta0 = built.data[o + 1];
            var omega = built.data[o + 2];
            var size = built.data[o + 3];
            var rgb = chan(built.data[o + 4]) + ", " + chan(built.data[o + 5]) + ", " + chan(built.data[o + 6]);
            ctx.strokeStyle = "rgba(" + rgb + ", 0.18)";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(cx, cy, r, theta0 - omega * 2 * CONFIG.tau, theta0);
            ctx.stroke();
            ctx.fillStyle = "rgba(" + rgb + ", 0.9)";
            ctx.beginPath();
            ctx.arc(
                cx + Math.cos(theta0) * r,
                cy + Math.sin(theta0) * r,
                Math.max(0.75, size * 0.35),
                0,
                Math.PI * 2
            );
            ctx.fill();
        }
    }

    var debouncedRender = debounce(render, CONFIG.resizeDebounceMs);
    var ro = null;
    if (typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(debouncedRender);
        ro.observe(section);
    } else {
        window.addEventListener("resize", debouncedRender);
    }

    render();

    return {
        destroy: function () {
            if (destroyed) return;
            destroyed = true;
            if (ro) ro.disconnect();
            else window.removeEventListener("resize", debouncedRender);
        },
    };
}

var instances = [];

export default function initStars() {
    instances.forEach(function (inst) {
        inst.destroy();
    });
    instances = [];
    var canvases = document.querySelectorAll(".call-to-action .stars-canvas");
    for (var i = 0; i < canvases.length; i++) {
        var inst = createStarfield(canvases[i]);
        if (inst) instances.push(inst);
    }
}
